
/**
 * Megamenu
 * Dropdown menu functionality made with accessibility in mind
 * Moves focus to the dropdown and back.
 *
 * Markup example:
 * <nav>
 *  <ul>
 *    <li><button class="js-dropdown-toggle" data-dropdown-group="navbar" aria-controls="mega-palvelut">Palvelut<button></li>
 *    <li>...</li>
 *  </ul>
 *  <div class="megamenu__content-wrapper">
 *    <span class="js-back-to-opener" tabindex="-1"></span>
 *    <div class="megamenu__content" id="mega-palvelut">
 *    ...
 *    </div>
 *    <div class="megamenu__content" id="mega-tuotteet">
 *      ...
 *    </div>
 *    <span class="js-back-to-opener" tabindex="-1"></span>
 *  </div>
 * </nav>
 * Optional helper attributess to dropdown toggle:
 * data-dropdown-group="navbar" - if there are multiple dropdowns, this will close all others in the same group
 * data-wrapper="parent" - if the dropdown is not directly under the toggle button, this will find  closest wrapper with .js-toggle-wrapper class
 * data-level="1" - if this is bigger than 0, closing the dropdown will not remove body class
 */
export default class MegaMenuDropdowns {

  /**
   * Constructor
   * @param {HTMLElement} container Area where the functionality is activated
   * @param {Object} config Configuration
   */
  constructor(container, config) {
    const defaults = {
      openOnHover: false,
      closeOnMouseOut: false,
      bodyClass: '' //class for body when dropdown is open
    };

    this.container = container;
    this.config = Object.assign({}, defaults, config);
    this.dropdownToggles = container.querySelectorAll('.js-dropdown-toggle'); // buttons that will open dropdown
    this.backTriggers = this.container.querySelectorAll('.js-back-to-opener'); //triggers that will return focus back to navigation
    this.closeButton = this.container.querySelector('.js-dropdown-closer');
    this.hoverTimer = null;
    this.activeToggle = null;

    //binding 'this' here, to make adding/removing event listeners easier.
    this._onKeyPress = this._onKeyPress.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
  }

  /**
   * Attach click event listeners to link area
   */
  init() {
    this.dropdownToggles.forEach((toggle) => {
      const toggleParent = toggle.parentElement;
      toggle.setAttribute('aria-expanded', false); //tells screenreaders that drodown is closed
      toggle.addEventListener('click', this._dropdownToggle.bind(this));

      if(this.config.openOnHover) {
        toggleParent.addEventListener('mouseover', this._onMouseOver.bind(this));
      }

      if(this.config.openOnHover && this.config.closeOnMouseOut) {
        toggleParent.addEventListener('mouseleave', this._onMouseLeave.bind(this));
      }
    });

    //if there is close button
    if(this.closeButton) {
      this.closeButton.addEventListener('click', (event) => {
        if(this.activeToggle) {
          this.activeToggle.focus();
          this._closeDropdown(this.activeToggle);
        }
      });
    }

    //If user tabs outside the dropdown content, we will send the focus back to the dropdown toggle
    this.backTriggers.forEach((backTrigger) => {
      backTrigger.addEventListener('focus', (event) => {
        if(this.activeToggle) {
          this.activeToggle.focus();
          this._closeDropdown(this.activeToggle);
        }
      });
    });
  }

  /**
   * Add listeners that close dropdown
   */
  _addDocumentListeners() {
    //click outside area will close all dropdowns
    document.addEventListener('click', this._onDocumentClick);
    //Pressing esc will close all dropdowns
    document.addEventListener('keydown', this._onKeyPress);
  }

  /**
   * Remove listeners that close dropdown
   */
  _removeDocumentListeners() {
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener('keydown', this._onKeyPress);
  }


  /**
   * Toggle dropdown
   * @param {Event} event Click event
   */
  _dropdownToggle(event) {
    const toggle = event.currentTarget;
    if(toggle.getAttribute('aria-expanded') === 'true') {
      this._closeDropdown(toggle);
    } else {
      this._openDropdown(toggle);
    }
  }

  /**
   * Open dropdown on mouse over
   * @param {Event} event mouseover event
   */
  _onMouseOver(event) {
    const toggle = event.currentTarget.querySelector('.js-dropdown-toggle');
    this._openDropdown(toggle);
  }

  /**
   * Close dropdown on mouse out
   * @param {Event} event mouseleave event
   */
  _onMouseLeave(event) {
    const toggle = event.currentTarget.querySelector('.js-dropdown-toggle');
    this.hoverTimer = setTimeout(() => this._closeDropdown(toggle));
  }

  /**
   * Checks if click event happaned outside the container
   * @param {Event} event click event
   */
  _onDocumentClick(event) {
    if(!this.container.contains(event.target)) {
      this._closeAllDropdowns();
    }
  }

  /**
   * Checks if pressed key was esc
   * @param {Event} event key press event
   */
  _onKeyPress(event) {
    if(event.key === 'Escape') {
      //if focus is inside menu area, move it to the active toggle
      const activeToggle = this.container.querySelector('.js-dropdown-toggle[aria-expanded="true"]');
      if(this.container.contains(document.activeElement) && activeToggle) {
        activeToggle.focus();
      }
      this._closeAllDropdowns();
    }
  }

  /**
   * Open dropdown
   * Adds class 'open' to the dropdown, set state for the toggle
   * @param {HTMLElement} toggle element that activated the dropdown
   */
  _openDropdown(toggle) {
    const dropdownGroup = toggle.dataset.dropdownGroup;
    if(dropdownGroup) {
      this._closeAllDropdowns(dropdownGroup);
    }

    clearTimeout(this.hoverTimer); //in case mouse-leave event is triggered, cancel it

    const dropdownId = toggle.getAttribute('aria-controls');
    //if dropdown is not directly under the toggle button, we can find it with aria-controls attribute
    let dropdown = dropdownId ? document.getElementById(dropdownId) : toggle.nextElementSibling;
    //special case for dropdowns that are not directly under the toggle button
    if(!dropdown && toggle.dataset.wrapper === 'parent') {
      const parent = toggle.closest('.js-toggle-wrapper');
      dropdown = parent ? parent.nextElementSibling : null;
    }

    toggle.classList.add('selected');
    toggle.setAttribute('aria-expanded', true); //this tells screenreaders that dropdown is open

    if(!dropdown) {
      return;
    }

    dropdown.classList.add('open');
    setTimeout(() => {
      dropdown.classList.add('animated');
    }, 100);

    this._addDocumentListeners(); //adds listeners for ESC or clickin outside the area

    if(this.config.bodyClass && toggle.dataset.bodyclass!=='false') {
      document.body.classList.add(this.config.bodyClass);
    }

    if(dropdownId) {
      this._makeElementFocusable(this.backTriggers);
      this._moveFocusToDropdown(dropdown, toggle);
    }

  }

  /**
   * Close dropdown
   * Removes class 'open' from the dropdown, set state for the toggle
   * @param {HTMLElement} toggle - element that activated the dropdown
   */
  _closeDropdown(toggle) {
    const delay = toggle.dataset.animationDelay || 0;
    const dropdownId = toggle.getAttribute('aria-controls');

    //if dropdown is not directly under the toggle button, we can find it with aria-controls attribute
    let dropdown = dropdownId ? document.getElementById(dropdownId) : toggle.nextElementSibling;
    //special case for dropdowns that are not directly under the toggle button
    if(!dropdown && toggle.dataset.wrapper === 'parent') {
      const parent = toggle.closest('.js-toggle-wrapper');
      dropdown = parent ? parent.nextElementSibling : null;
    }

    if(!dropdown) {
      return;
    }

    toggle.classList.remove('selected');
    toggle.setAttribute('aria-expanded', false);

    dropdown.classList.remove('animated');
    setTimeout(() => {
      dropdown.classList.remove('open');
    }, parseInt(delay));

    this._removeDocumentListeners();
    this.activeToggle = null;

    if(dropdownId) {
      this._makeElementUnfocusable(this.backTriggers);
    }

    if(this.config.bodyClass && (!toggle.dataset.level || toggle.dataset.level === 0)) {
      document.body.classList.remove(this.config.bodyClass);
    }
  }

  /**
  * Close dropdowns
  * @param {String} dropdownGroup - id defined, close only dropdowns that are in the same group
  */
  _closeAllDropdowns(dropdownGroup ='') {
    this.dropdownToggles.forEach((toggle) => {
      if(dropdownGroup && toggle.dataset.dropdownGroup !== dropdownGroup) {
        return;
      }
      if(toggle.getAttribute('aria-expanded') === 'true') {
        this._closeDropdown(toggle);
      }
    });
  }

  /**
   * Moves focus to dropdown
   * @param {HTMLElement} dropdown element that will receive focus
   * @param {HTMLElement} toggle button that triggered this action
   */
  _moveFocusToDropdown(dropdown, toggle) {
    console.log(dropdown);
    this.activeToggle = toggle; //store active toggle
    //Move focus to itself or to the first focusable element inside
    const isSelfFocusable = dropdown.getAttribute( 'tabindex' ) === '-1' || dropdown.getAttribute( 'tabindex' ) === '0';
    const focusable = isSelfFocusable ? dropdown : this._getKeyboardFocusableElement(dropdown);
    if(focusable) {
      focusable.focus();
      console.log(document.activeElement);
    }
  }

  /**
  * Gets keyboard-focusable elements within a specified element
  * @param {HTMLElement} element element
  * @returns {HTMLElement}
  */
  _getKeyboardFocusableElement(element) {
    return [...element.querySelectorAll(
      'a, button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])'
    )].find(el => !el.hasAttribute('disabled') && getComputedStyle(el).display !== 'none' );
  }

  /**
   * Make element/elements focusable by adding tabindex=0
   * @param {HTMLElement | NodeList} element element/elements picked with queryslector/queryselectorAll
   */
  _makeElementFocusable(element){
    if(element.length){
      element.forEach((singleElement) => {
        singleElement.setAttribute('tabindex', 0);
      });
    } else if(element){
      element.setAttribute('tabindex', 0);
    }
  }

  /**
   * Make element unfocusable by adding tabindex="-1"
   * @param {HTMLElement | NodeList} element element/elements picked with queryslector/queryselectorAll
   */
  _makeElementUnfocusable(element){
    if(element.length){
      element.forEach((singleElement) => {
        singleElement.setAttribute('tabindex', '-1');
      });
    } else if(element){
      element.setAttribute('tabindex', '-1');
    }
  }

}
